import { Fade } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { createStyles } from '@material-ui/styles';
import { logout as logoutAction, withAuth0 } from '@topconagriculture/auth';
import { configureModalMap, useModal, withModal } from '@topconagriculture/modal';
import { ICONS, withBaseProps } from '@topconagriculture/react-components';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { getLocale, getTenantSettings, getUserCompany, getUserProfile } from '../../store';
import { UserModal } from '../UserModal';
import UserMenuButton from './UserMenuButton';
import UserMenuHeader from './UserMenuHeader';
import UserMenuTrigger from './UserMenuTrigger';
import UserReportButton from './UserReportButton';
import { NotificationModal, useReadPlatformNotifications, } from '@topconagriculture/platform-notifications';
import { FiscalEntities } from '../../api';
const styles = (theme) => createStyles({
    root: {
        marginLeft: 20,
        marginRight: 20,
        position: 'relative',
    },
    popoverWrapper: {
        position: 'absolute',
        width: 300,
        backgroundColor: theme.color.background.light,
        right: '-30%',
        top: '135%',
        borderRadius: theme.common.borderRadius.default,
        boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
        zIndex: 9999,
        color: theme.color.font.dark,
    },
    popoverArrow: {
        position: 'absolute',
        height: 0,
        width: 0,
        borderWidth: 8,
        top: -15,
        right: 24,
        borderColor: 'transparent',
        borderBottomColor: theme.color.primary.light,
        borderStyle: 'solid',
        zIndex: 10000,
    },
    buttonsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    modal: {
        zIndex: '1500 !important',
    },
});
export var ProfilingModals;
(function (ProfilingModals) {
    ProfilingModals["editProfile"] = "editProfile";
    ProfilingModals["notificationModal"] = "notificationModal";
})(ProfilingModals || (ProfilingModals = {}));
const profilingModalMap = {
    [ProfilingModals.editProfile]: React.createElement(UserModal, null),
    [ProfilingModals.notificationModal]: React.createElement(NotificationModal, null),
};
const ACCOUNT_APP_ID = '5e6771ad10414c64785cd037';
configureModalMap(profilingModalMap);
const UserMenu = (props) => {
    const { classes, children, openModal, t, auth, getHelpUrl, sentryInstance, serviceAgreementsUrl, policiesUrl, connectionsUrl, disableEditUserSettings = false, configNotification, activeIndexList = [], } = props;
    const dispatch = useDispatch();
    const { dispatchOpenModal } = useModal();
    const onLogout = useCallback(() => {
        dispatch(logoutAction.started({}));
        auth.logout();
    }, [dispatch, auth]);
    const onStopImpersonate = useCallback(() => {
        auth.stopImpersonating();
    }, [dispatch, auth]);
    const [popoverOpened, setPopoverOpened] = useState(false);
    const handleOpen = useCallback(() => {
        setPopoverOpened(!popoverOpened);
    }, [setPopoverOpened, popoverOpened]);
    const handleClose = useCallback(() => {
        setPopoverOpened(false);
    }, [setPopoverOpened]);
    const handleOpenSettingModal = useCallback(() => {
        setPopoverOpened(false);
        openModal({
            modalName: ProfilingModals.editProfile,
        });
    }, [setPopoverOpened]);
    const openLink = useCallback((url) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(url, '_blank');
    }, []);
    const userLocale = useSelector(getLocale);
    const { readNotificationsIds } = useReadPlatformNotifications();
    const notReadNotificationIds = React.useMemo(() => activeIndexList.filter((active) => !readNotificationsIds.includes(active)), [readNotificationsIds, activeIndexList]);
    const userProfile = useSelector(getUserProfile);
    const userCompany = useSelector(getUserCompany);
    const tenantSettings = useSelector(getTenantSettings);
    const tenantApps = tenantSettings === null || tenantSettings === void 0 ? void 0 : tenantSettings.apps;
    const accountApp = tenantApps === null || tenantApps === void 0 ? void 0 : tenantApps.find((app) => app.id === ACCOUNT_APP_ID);
    const exportDataUrl = (accountApp === null || accountApp === void 0 ? void 0 : accountApp.url) ? `${accountApp.url}/data-export` : undefined;
    const canExportData = (userCompany === null || userCompany === void 0 ? void 0 : userCompany.billing.fiscalEntity) === FiscalEntities.TA;
    if (!userProfile) {
        return null;
    }
    return (React.createElement(ClickAwayListener, { onClickAway: handleClose },
        React.createElement("div", { className: classes.root },
            React.createElement(UserMenuTrigger, { badge: notReadNotificationIds.length > 0, user: userProfile, handleOpen: handleOpen }),
            popoverOpened && (React.createElement(Fade, { in: true },
                React.createElement("div", { className: classes.popoverWrapper, "data-testid": 'user-menu-popover' },
                    React.createElement("div", { className: classes.popoverArrow }),
                    React.createElement(UserMenuHeader, { user: userProfile }),
                    React.createElement("div", { className: classes.buttonsWrapper },
                        !disableEditUserSettings && (React.createElement(UserMenuButton, { icon: 'settings', onClick: handleOpenSettingModal, testId: 'settings' }, t('common:user_settings'))),
                        connectionsUrl && (React.createElement(UserMenuButton, { icon: 'share2', onClick: openLink(connectionsUrl), testId: 'connections' }, t('common:connections'))),
                        children,
                        getHelpUrl && (React.createElement(UserMenuButton, { icon: 'support', onClick: openLink(getHelpUrl), testId: 'help' }, t('common:get_help'))),
                        serviceAgreementsUrl && (React.createElement(UserMenuButton, { icon: 'recipe', onClick: openLink(serviceAgreementsUrl), testId: 'agreements' }, t('common:service_agreements'))),
                        policiesUrl && (React.createElement(UserMenuButton, { icon: 'recipe', onClick: openLink(policiesUrl), testId: 'policies' }, t('common:policies'))),
                        canExportData && exportDataUrl && (React.createElement(UserMenuButton, { icon: 'zip', onClick: openLink(exportDataUrl), testId: 'exportData' }, t('common:export_data'))),
                        configNotification && (React.createElement(UserMenuButton, { icon: ICONS.alarm, badge: notReadNotificationIds.length, onClick: () => dispatchOpenModal({
                                modalName: ProfilingModals.notificationModal,
                                modalParams: {
                                    config: configNotification,
                                    activeIndexList,
                                    userLocale,
                                },
                                modalOptions: {
                                    closeButtonIsVisible: true,
                                    modalDialogProps: {
                                        maxWidth: false,
                                        className: classes.modal,
                                    },
                                },
                            }), testId: 'notification-modal' }, t('common:platform_notifications.notifications'))),
                        React.createElement(UserReportButton, { sentryInstance: sentryInstance }),
                        auth.isImpersonating ? (React.createElement(UserMenuButton, { icon: 'logout', onClick: onStopImpersonate, testId: 'stop-impersonate' }, t('common:impersonate:stop'))) : (React.createElement(UserMenuButton, { icon: 'logout', onClick: onLogout, testId: 'logout' }, t('common:logout'))))))))));
};
export default compose(withBaseProps({ styles, translations: ['common'] }), withAuth0, withModal)(UserMenu);
//# sourceMappingURL=UserMenu.js.map